import { useState } from 'react'
import Seo from '../components/Seo.jsx'
import Container from '../components/Container.jsx'
import { Plus, Minus, Search } from 'lucide-react'

const FAQ_DATA = [
  {
    category: 'General',
    items: [
      {
        q: 'What is Spectranox and what services do you provide?',
        a: 'Spectranox is a premium, full-service technology agency specializing in high-performance web development, mobile applications, bespoke software design, AI integrations, cybersecurity audits, and advanced drone/IoT engineering. We deliver end-to-end solutions for seed-stage startups and global enterprises.'
      },
      {
        q: 'Where is Spectranox located and do you support global clients?',
        a: 'We are headquartered in India with support networks across multiple countries. We regularly collaborate with international clients across the US, EU, Middle East, and Asia, coordinating seamlessly via our centralized online client portal.'
      }
    ]
  },
  {
    category: 'Development & Services',
    items: [
      {
        q: 'What technology stacks do you support?',
        a: 'We leverage modern, production-grade technologies tailored to project requirements. Our web team works with React, Next.js, Node.js, and high-performance server stacks. For mobile apps, we deploy native iOS/Android or cross-platform codebases. IoT and embedded systems are developed on ESP32, STM32, ARM, and FPGA platforms.'
      },
      {
        q: 'Do you provide database setups and backend integration?',
        a: 'Yes. We build robust backend microservices, custom APIs, and database schemas (including PostgreSQL, MongoDB, and MySQL databases) that can be run on local environments like XAMPP or scaled to enterprise cloud environments like AWS, Azure, or GCP.'
      },
      {
        q: 'Do you offer cyber security auditing and penetration testing?',
        a: 'Absolutely. We provide end-to-end security audits, source code analysis, penetration testing, database hardening, and compliance support (including GDPR, HIPAA, and ISO 27001 readiness) to protect your systems from vulnerabilities.'
      }
    ]
  },
  {
    category: 'Project Management & Support',
    items: [
      {
        q: 'How does the client portal work?',
        a: 'Once your project begins, you receive login credentials to our custom Client Portal. Inside, you can track progress milestones, browse service expansions, communicate directly with your dedicated development team, schedule meetings, and manage billing.'
      },
      {
        q: 'What kind of support is offered after deployment?',
        a: 'We provide an initial 30-day post-launch warranty covering all bugs, performance issues, or hosting configurations related to the SOW. Beyond that, clients can opt for our 24/7 dedicated support SLA which grants round-the-clock access to senior engineers.'
      }
    ]
  },
  {
    category: 'Billing & Refunds',
    items: [
      {
        q: 'What is your refund policy?',
        a: 'Payments are milestones-based. Once a project is fully completed and delivered to the client, payments are non-refundable. If a cancellation is requested mid-project, we charge pro-rata for work completed up to that point and refund the remainder, minus any licensing and raw material costs.'
      },
      {
        q: 'Can I request a custom quote for a complex project?',
        a: 'Yes. You can contact us directly via our Request a Quote button or email contact forms. A technical solutions architect will review your specifications and schedule a discovery call within 24 hours.'
      }
    ]
  }
]

export default function Faq() {
  const [search, setSearch] = useState('')
  const [activeIdx, setActiveIdx] = useState(null)
  const [selectedCat, setSelectedCat] = useState('All')

  // Filter FAQ items
  const filteredData = FAQ_DATA.map(group => {
    if (selectedCat !== 'All' && group.category !== selectedCat) return null;
    const items = group.items.filter(item => 
      item.q.toLowerCase().includes(search.toLowerCase()) || 
      item.a.toLowerCase().includes(search.toLowerCase())
    );
    if (items.length === 0) return null;
    return { ...group, items };
  }).filter(Boolean);

  const toggleAccordion = (qIndex) => {
    setActiveIdx(activeIdx === qIndex ? null : qIndex);
  }

  const categories = ['All', 'General', 'Development & Services', 'Project Management & Support', 'Billing & Refunds'];

  let runningIndex = 0;

  return (
    <>
      <Seo
        title="Frequently Asked Questions (FAQs)"
        description="Find answers to common questions about Spectranox services, billing, development timeline, and support."
        path="/faqs"
      />
      <main className="min-h-screen bg-[#020617] pt-32 pb-24 text-slate-100 antialiased">
        <Container className="max-w-4xl">
          <div className="text-center">
            <span className="font-mono text-xs uppercase tracking-widest text-blue-500">Resource Center</span>
            <h1 className="mt-3 font-display text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
              Frequently Asked Questions
            </h1>
            <p className="mt-4 mx-auto max-w-xl text-sm leading-relaxed text-slate-400">
              Everything you need to know about our technology solutions, development lifecycle, support agreements, and pricing structures.
            </p>
          </div>

          {/* Search bar */}
          <div className="mt-10 relative max-w-md mx-auto">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500" />
            <input
              type="text"
              placeholder="Search questions..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-12 pr-4 py-3 rounded-full border border-slate-800 bg-[#070b1e]/60 text-slate-200 placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            />
          </div>

          {/* Category Tabs */}
          <div className="mt-8 flex flex-wrap justify-center gap-2">
            {categories.map(cat => (
              <button
                key={cat}
                type="button"
                onClick={() => { setSelectedCat(cat); setActiveIdx(null); }}
                className={`rounded-full px-4 py-2 text-xs font-semibold tracking-wide transition ${
                  selectedCat === cat 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-slate-900 text-slate-400 hover:bg-slate-800'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Accordion lists */}
          <div className="mt-12 space-y-10">
            {filteredData.length > 0 ? (
              filteredData.map((group, groupIdx) => {
                return (
                  <div key={groupIdx} className="space-y-4">
                    <h2 className="text-lg font-bold text-blue-400 border-b border-slate-800 pb-2 uppercase tracking-wider font-mono">
                      {group.category}
                    </h2>
                    <div className="space-y-2">
                      {group.items.map((item, itemIdx) => {
                        const globalIdx = `${groupIdx}-${itemIdx}`;
                        const isOpen = activeIdx === globalIdx;
                        return (
                          <div 
                            key={itemIdx} 
                            className="rounded-2xl border border-slate-800/80 bg-[#070b1e]/30 transition-all duration-300 overflow-hidden"
                          >
                            <button
                              type="button"
                              onClick={() => toggleAccordion(globalIdx)}
                              className="w-full flex items-center justify-between p-5 text-left font-semibold text-white/90 hover:text-white"
                            >
                              <span>{item.q}</span>
                              <span className="ml-4 shrink-0 p-1.5 rounded-full bg-slate-900 border border-slate-800">
                                {isOpen ? <Minus className="h-4 w-4 text-blue-400" /> : <Plus className="h-4 w-4 text-slate-400" />}
                              </span>
                            </button>
                            <div 
                              className={`transition-all duration-300 ease-in-out ${
                                isOpen ? 'max-h-[300px] border-t border-slate-800/50 p-5 opacity-100' : 'max-h-0 opacity-0 pointer-events-none'
                              }`}
                            >
                              <p className="text-sm leading-relaxed text-slate-300">{item.a}</p>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )
              })
            ) : (
              <div className="text-center py-16">
                <p className="text-slate-400 text-base">No matches found for your search query.</p>
              </div>
            )}
          </div>
        </Container>
      </main>
    </>
  )
}
